import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AuthContext from "../components/shared/AuthContext";
import OrgContext from "../organisation/OrgContext";
import smalLogo from "../assets/images/ElmanagerLogo.png";
import { baseURL } from "../services/api";
import Documents from "../components/Documents";

import Tooltip from "@mui/material/Tooltip";

import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import TurnedInIcon from "@mui/icons-material/TurnedIn";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import { useSidebar } from "../components/shared/SidebarContext";


/* ------------------------------------------------------------------ */
/* Small presentational components                                     */
/* ------------------------------------------------------------------ */

function NavRow({ item, active, collapsed }) {
  const Icon = item.icon;
  const className = `uui-item${active ? " is-active" : ""}`;
  const content = (
    <>
      <Icon className="uui-item__icon" />
      {!collapsed && <span className="uui-item__label">{item.label}</span>}
    </>
  );

  return (
    <Tooltip title={collapsed ? item.label : ""} placement="right" arrow>
      {item.href ? (
        <Link to={item.href} className={className}>
          {content}
        </Link>
      ) : (
        <button type="button" className={className} onClick={item.onClick}>
          {content}
        </button>
      )}
    </Tooltip>
  );
}

function NavGroup({ item, collapsed, open, hasActive, activeHref, onToggle }) {
  const Icon = item.icon;

  return (
    <div className="uui-group">
      <Tooltip title={collapsed ? item.label : ""} placement="right" arrow>
        <button
          type="button"
          className={`uui-item${hasActive ? " is-active" : ""}`}
          onClick={onToggle}
          aria-expanded={open}
        >
          <Icon className="uui-item__icon" />
          {!collapsed && (
            <>
              <span className="uui-item__label">{item.label}</span>
              <ExpandMoreOutlinedIcon
                className={`uui-item__chevron${open ? " is-open" : ""}`}
              />
            </>
          )}
        </button>
      </Tooltip>

      {!collapsed && open && (
        <ul className="uui-sub">
          {item.children.map((child) => (
            <li key={child.href}>
              <Link
                to={child.href}
                className={`uui-sub__item${
                  child.href === activeHref ? " is-active" : ""
                }`}
              >
                {child.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sidebar                                                             */
/* ------------------------------------------------------------------ */

function SideBar() {
  const {
    user,
    logout,
    isAdmin,
    isCommercial,
    isComptableAndCommercial,
    isComptable,
  } = useContext(AuthContext);
  const { organisation } = useContext(OrgContext);
  const { pathname } = useLocation();
  const [t] = useTranslation();

  const {collapsed, setCollapsed} = useSidebar();
  const [query, setQuery] = useState("");
  const [openGroups, setOpenGroups] = useState({});
  const [documentsOpen, setDocumentsOpen] = useState(false);

  const hasOrg = Boolean(organisation);
  const canSell = isAdmin || isCommercial || isComptableAndCommercial;
  const canAccount = isAdmin || isComptable || isComptableAndCommercial;

  const handleLogout = (event) => {
    event.preventDefault();
    logout();
  };

  /* ---------------- Main navigation ---------------- */

  const mainItems = [
    {
      key: "dashboard",
      label: t("Dashboard"),
      href: "/",
      icon: GridViewOutlinedIcon,
      visible: hasOrg,
    },
    // {
    //   key: "organisations",
    //   label: t("Organisations"),
    //   href: "/organisations",
    //   icon: CorporateFareOutlinedIcon,
    //   visible: isAdmin,
    // },
    {
      key: "articles",
      label: t("Articles"),
      href: "/items",
      icon: LocalShippingOutlinedIcon,
      visible: hasOrg && canSell,
    },
    {
      key: "ventes",
      label: t("Ventes"),
      icon: ShoppingCartOutlinedIcon,
      visible: hasOrg && canSell,
      children: [
        { label: t("Clients"), href: "/clients" },
        { label: t("Factures de vente"), href: "/sales/invoices" },
        { label: t("Devis"), href: "/sales/devis" },
        { label: t("Facture d'avoir"), href: "/sales/factureavoir" },
      ],
    },
    {
      key: "achats",
      label: t("Achats"),
      icon: ShoppingBagOutlinedIcon,
      visible: hasOrg && canSell,
      children: [
        { label: t("Fournisseurs"), href: "/fournisseurs" },
        { label: t("Facture lié au stock"), href: "/achat/factureachat" },
        { label: t("Facture de dépense"), href: "/depense/facturedepense" },
        { label: t("Bon de commande"), href: "/achats/boncommande" },
      ],
    },
    {
      key: "finance",
      label: t("Finance"),
      icon: AccountBalanceIcon,
      visible: hasOrg,
      children: [
        { label: t("Trésorerie"), href: "/tresorie", visible: canSell },
        { label: t("Banque"), href: "/bq", visible: canAccount },
        { label: t("Caisse"), href: "/cs", visible: canAccount },
        { label: t("Relevé Bancaire"), href: "/rlvBq", visible: canAccount },
      ],
    },
    {
      key: "comptable",
      label: t("Comptable"),
      icon: BarChartOutlinedIcon,
      visible: hasOrg && canAccount,
      children: [
        { label: t("Plan Comptable"), href: "/plancomptable" },
        { label: t("Ecriture Comptable"), href: "/plancomptable/ec" },
        { label: t("Journal"), href: "/comptabilite/journal" },
        { label: t("Tableau de TVA"), href: "/comptabilite/tvaTable" },
        { label: t("Cpc"), href: "/cpc" },
        { label: t("Bilan"), href: "/bilan" },
      ],
    },
    {
      key: "pieces",
      label: t("Pieces"),
      href: "/piece",
      icon: TurnedInIcon,
      visible: hasOrg && canAccount,
    },
    {
      key: "recurrentes",
      label: t("Récurrente Facture"),
      href: "/factures_recurrentes",
      icon: SettingsBackupRestoreIcon,
      visible: hasOrg && canAccount,
    },
    {
      key: "brouillons",
      label: t("Brouillon scan"),
      href: "/factures_brouillons",
      icon: DeleteSweepOutlinedIcon,
      visible: true,
    },
  ];

  /* ---------------- Secondary navigation ---------------- */

  const otherItems = [
    {
      key: "emails",
      label: t("E-mails"),
      href: "/modeles",
      icon: MailOutlineOutlinedIcon,
      visible: hasOrg,
    },
    {
      key: "devises",
      label: t("Devises"),
      href: "/devises",
      icon: AttachMoneyOutlinedIcon,
      visible: hasOrg,
    },
    {
      key: "taxes",
      label: t("Impôt"),
      href: "/taxes",
      icon: AccountBalanceIcon,
      visible: hasOrg,
    },
    {
      key: "modele",
      label: t("Modèle de facture"),
      href: "/models/invoice",
      icon: DescriptionOutlinedIcon,
      visible: hasOrg,
    },
    {
      key: "calendrier",
      label: t("Calendrier"),
      href: "/calendar",
      icon: CalendarMonthOutlinedIcon,
      visible: hasOrg,
    },
    {
      key: "team",
      label: t("Gestion d'équipe"),
      href: "/team",
      icon: GroupOutlinedIcon,
      visible: hasOrg && isAdmin,
    },
    {
      key: "history",
      label: t("Historique"),
      href: "/History",
      icon: HistoryOutlinedIcon,
      visible: hasOrg && isAdmin,
    },
    {
      key: "tickets",
      label: t("Tickets"),
      href: "/tickets",
      icon: ErrorOutlineOutlinedIcon,
      visible: true,
    },
    {
      key: "documents",
      label: t("Documents"),
      icon: FolderOpenOutlinedIcon,
      onClick: () => setDocumentsOpen(true),
      visible: hasOrg,
    },
  ];

  /* ---------------- Visibility, active route, search ---------------- */

  const prepare = (items) =>
    items
      .filter((item) => item.visible !== false)
      .map((item) =>
        item.children
          ? {
              ...item,
              children: item.children.filter((c) => c.visible !== false),
            }
          : item
      )
      .filter((item) => !item.children || item.children.length > 0);

  const visibleMain = prepare(mainItems);
  const visibleOther = prepare(otherItems);

  // The most specific matching href wins (e.g. /plancomptable/ec over /plancomptable)
  const allHrefs = [...visibleMain, ...visibleOther].flatMap((item) =>
    item.children ? item.children.map((c) => c.href) : item.href ? [item.href] : []
  );
  const activeHref = allHrefs
    .filter((href) =>
      href === "/"
        ? pathname === "/"
        : pathname === href || pathname.startsWith(`${href}/`)
    )
    .sort((a, b) => b.length - a.length)[0];

  const q = query.trim().toLowerCase();
  const matches = (label) => label.toLowerCase().includes(q);

  const applySearch = (items) => {
    if (!q) return items;
    return items
      .map((item) => {
        if (!item.children) return matches(item.label) ? item : null;
        if (matches(item.label)) return item;
        const children = item.children.filter((c) => matches(c.label));
        return children.length ? { ...item, children } : null;
      })
      .filter(Boolean);
  };

  const shownMain = applySearch(visibleMain);
  const shownOther = applySearch(visibleOther);

  /* ---------------- Rendering helpers ---------------- */

  const renderItem = (item) => {
    if (!item.children) {
      return (
        <NavRow
          key={item.key}
          item={item}
          active={item.href === activeHref}
          collapsed={collapsed}
        />
      );
    }

    const hasActive = item.children.some((c) => c.href === activeHref);
    const stored = openGroups[item.key];
    const open = q ? true : stored !== undefined ? stored : hasActive;

    const handleToggle = () => {
      if (collapsed) {
        setCollapsed(false);
        setOpenGroups((prev) => ({ ...prev, [item.key]: true }));
        return;
      }
      setOpenGroups((prev) => ({ ...prev, [item.key]: !open }));
    };

    return (
      <NavGroup
        key={item.key}
        item={item}
        collapsed={collapsed}
        open={open}
        hasActive={hasActive}
        activeHref={activeHref}
        onToggle={handleToggle}
      />
    );
  };


  return (
    <>
      <aside className={`uui-sidebar${collapsed ? " is-collapsed" : ""}`}>
        {/* Header */}
        <div className="uui-header">
          <Link to="/" className="uui-logo-link">
          <img src={smalLogo} alt="Logo" className="uui-logo" />
          </Link>
          <button
            type="button"
            className="uui-collapse"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? t("Ouvrir le menu") : t("Réduire le menu")}
          >
            {collapsed ? <ChevronRightOutlinedIcon /> : <ChevronLeftOutlinedIcon />}
          </button>
        </div>

        {/* Search */}
        <div className="uui-search">
          {collapsed ? (
            <Tooltip title={t("Rechercher")} placement="right" arrow>
              <button
                type="button"
                className="uui-search__button"
                onClick={() => setCollapsed(false)}
                aria-label={t("Rechercher")}
              >
                <SearchOutlinedIcon />
              </button>
            </Tooltip>
          ) : (
            <label className="uui-search__field">
              <SearchOutlinedIcon className="uui-search__icon" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("Rechercher")}
              />
            </label>
          )}
        </div>

        {/* Navigation */}
        <nav className="uui-nav" aria-label="Navigation principale">
          {shownMain.length > 0 && (
            <div className="uui-section">
              {!collapsed && (
                <p className="uui-section__title">{t("Actions principales")}</p>
              )}
              {shownMain.map(renderItem)}
            </div>
          )}

          {shownOther.length > 0 && (
            <div className="uui-section">
              {collapsed ? (
                <hr className="uui-divider" />
              ) : (
                <p className="uui-section__title">{t("Autres actions")}</p>
              )}
              {shownOther.map(renderItem)}
            </div>
          )}

          {q && shownMain.length === 0 && shownOther.length === 0 && (
            <p className="uui-empty">{t("Aucun résultat")}</p>
          )}
        </nav>

        {/* Footer: organisation + logout */}
        <div className="uui-footer">
          {hasOrg && (
                <Link to="/organisations" className={`uui-org__link${pathname.startsWith("/organisations") ? " is-active" : ""}`} >
            <div className="uui-org" title={organisation.name}>
              <div className="uui-org__logo">
                <img
                  src={`${baseURL.replace("/api", "")}/upload/logo/${organisation.logo}`}
                  alt={organisation.name}
                />
              </div>
              {!collapsed && (
                <div className="uui-org__text">
                  <p className="uui-org__name">{organisation.name}</p>
                  <p className="uui-org__caption">{t("Organisation")}</p>
                </div>
              )}
            </div>
              </Link>
          )}

          <Tooltip title={collapsed ? t("deconnexion") : ""} placement="right" arrow>
            <button type="button" className="uui-logout" onClick={handleLogout}>
              <LogoutOutlinedIcon id="logout" className="uui-item__icon" />
              {!collapsed && (
                <span className="uui-logout__text">
                  <span className="uui-logout__label">{t("deconnexion")}</span>

                </span>
              )}
            </button>
          </Tooltip>
        </div>
      </aside>

      {hasOrg && (
        <Documents
          open={documentsOpen}
          handleClose={() => setDocumentsOpen(false)}
          organisationId={organisation.id}
        />
      )}
    </>
  );
}

export default SideBar;
